// ⚠️ please note that there are 3 datocms enums depending to some of these colors/colorThemes:

import { ColorThemeType, SpacingType } from "src/enums";

// 'Color Theme'/'Four Images Filler Color'/'Heading Color' 👈 handle changes with care
// TODO: cleanup these colors as part of https://github.com/peerigon/nextwww/issues/392
export const colors = {
	mint: "#46E1C8",
	/* mint shades */
	foam: "#F2FFFD",
	sea: "#E4F7F8",
	peppermint: "#BCE7E0",
	/* blue shades */
	ice: "#F5F9FF",
	moonRaker: "#BAD0F0",
	bluish: "#8194D8",
	blueberry: "#3026A4",
	nightSky: "#181B68",
	/* brown shades */
	beige: "#FBF9F8",
	rust: "#AF6C54",
	/* purple shades */
	plum: "#813B70",
	/* gray shades */
	gray: {
		line: "#C4C4C4",
		dark: "#6B6B6B",
		text: "#555555",
		nav: "#EAEAEA",
		// TODO: cleanup this color as part of https://github.com/peerigon/nextwww/issues/392
		"02": "#F4F4F4",
		"03": "#DFDFDF",
		"04": "#C0C0C0",
		"05": "#9A9A9A",
		"06": "#727272",
		"09": "#262626",
	},
	// TODO: cleanup these colors as part of https://github.com/peerigon/nextwww/issues/392
	amber: {
		"01": "#FEFBF0",
		"02": "#FFF7DB",
		"05": "#F6CE3C",
	},
	blueberryNew: {
		"01": "#F2F7FC",
		"02": "#E4ECF7",
		"05": "#7391D4",
	},
	mintNew: {
		"01": "#EFFAF5",
		"02": "#DFF6F0",
		"05": "#46E1C8",
	},
	white: "#fff",
	// use this instead of "transparent" in gradients because Safari 🤡
	// https://stackoverflow.com/a/46165475/4903358
	transparent: "rgba(255, 255, 255, 0)",
	black: "#000",
	almostBlack: "#0B0B0B",
	yellow: "#FFD873",
	yellowLight: "#FFF1CB",
	moonRakerLight: "#EAF1FB",
	peppermintLight: "#EBF8F6",
};

const colorTheme: Record<ColorThemeType, { fg: string; bg: string }> = {
	mint: {
		fg: colors.black,
		bg: colors.mint,
	},
	moonraker: {
		fg: colors.black,
		bg: colors.moonRaker,
	},
	almostBlack: {
		fg: colors.white,
		bg: colors.almostBlack,
	},
	black: {
		fg: colors.white,
		bg: colors.black,
	},
	blueberry: {
		fg: colors.white,
		bg: colors.blueberry,
	},
	beige: {
		fg: colors.black,
		bg: colors.beige,
	},
	white: {
		fg: colors.black,
		bg: colors.white,
	},
	foam: {
		fg: colors.black,
		bg: colors.foam,
	},
	ice: {
		fg: colors.black,
		bg: colors.ice,
	},
	yellow: {
		fg: colors.black,
		bg: colors.yellow,
	},
	bluish: {
		fg: colors.black,
		bg: colors.bluish,
	},
	sea: {
		fg: colors.black,
		bg: colors.sea,
	},
	peppermint: {
		fg: colors.black,
		bg: colors.peppermint,
	},
};

export const checkColorTheme = (
	colorThemeEnum: any,
): colorThemeEnum is keyof typeof colorTheme => colorThemeEnum in colorTheme;

export const getColorTheme = (
	colorThemeEnum: string,
): { fg: string; bg: string } =>
	colorThemeEnum && checkColorTheme(colorThemeEnum)
		? colorTheme[colorThemeEnum]
		: colorTheme.white;

export const fonts = {
	mono: "var(--font-silka)",
	adelphi: "var(--font-adelphi)",
	fontSize: {
		px11: "1.1rem",
		px12: "1.2rem",
		px13: "1.3rem",
		px14: "1.4rem",
		px16: "1.6rem", // default
		px18: "1.8rem",
		px20: "2rem",
		px24: "2.4rem",
		px28: "2.8rem",
		px32: "3.2rem",
		px35: "3.5rem",
		px40: "4rem",
		px44: "4.4rem",
		px68: "6.8rem",
		px100: "10rem",
		px140: "14rem",
		px110: "11rem",
		px220: "22rem",
	},
	lineHeight: {
		regular: "100%",
		regular1: "110%",
		regular2: "115%",
		regular3: "120%",
		hAddOn: "125%",
		medium: "130%",
		medium1: "135%",
		large: "140%",
		larger: "145%",
		large1: "148.5%",
		large2: "160%",
		paragraph: "156%",
	},
	fontWeight: {
		lighter: 265,
		light: 300,
		regular: 400,
		strong: 440,
		stronger: 480,
		medium: 500,
		bold: 600,
		bolder: 700,
		mediumBold: 780,
		extraBold: 800,
		black: 890,
	},
	letterSpacing: {
		narrow: "-0.02em",
		tiny: "0.04em",
		wide: "0.08em",
		wider: "0.096em",
		widest: "0.1em",
		button: "0.12em",
		hAddOn: "0.14em",
	},
};

export const textPresets = {
	size1: {
		fontSize: fonts.fontSize.px40,
		fontSizeLg: fonts.fontSize.px68,
		fontWeight: fonts.fontWeight.bold,
		lineHeight: fonts.lineHeight.regular1,
		letterSpacing: "0",
		textTransform: "none",
	},
	size2: {
		fontSize: fonts.fontSize.px28,
		fontSizeLg: fonts.fontSize.px44,
		fontWeight: fonts.fontWeight.bold,
		lineHeight: fonts.lineHeight.regular1,
		letterSpacing: "0",
		textTransform: "none",
	},
	size3: {
		fontSize: fonts.fontSize.px24,
		fontSizeLg: fonts.fontSize.px32,
		fontWeight: fonts.fontWeight.bold,
		lineHeight: fonts.lineHeight.regular3,
		letterSpacing: "0",
		textTransform: "none",
	},
	size4: {
		fontSize: fonts.fontSize.px20,
		fontSizeLg: fonts.fontSize.px24,
		fontWeight: fonts.fontWeight.bold,
		lineHeight: fonts.lineHeight.regular3,
		letterSpacing: "0",
		textTransform: "none",
	},
	size5: {
		fontSize: fonts.fontSize.px16,
		fontSizeLg: fonts.fontSize.px20,
		fontWeight: fonts.fontWeight.bold,
		lineHeight: fonts.lineHeight.regular2,
		letterSpacing: fonts.letterSpacing.wider,
		textTransform: "uppercase",
	},
	size6: {
		fontSize: fonts.fontSize.px16,
		fontSizeLg: fonts.fontSize.px20,
		fontWeight: fonts.fontWeight.regular,
		lineHeight: fonts.lineHeight.regular2,
		letterSpacing: fonts.letterSpacing.wider,
		textTransform: "uppercase",
	},
	// Paragraph
	size7: {
		fontSize: fonts.fontSize.px18,
		fontSizeLg: fonts.fontSize.px20,
		fontWeight: fonts.fontWeight.light,
		lineHeight: fonts.lineHeight.larger,
		letterSpacing: "0",
		textTransform: "none",
	},
	// Small Paragraph
	size8: {
		fontSize: fonts.fontSize.px14,
		fontSizeLg: fonts.fontSize.px16,
		fontWeight: fonts.fontWeight.light,
		lineHeight: fonts.lineHeight.larger,
		letterSpacing: "0",
		textTransform: "none",
	},
};

export const shadows = {
	light: "0px 0px 35px rgba(0, 0, 0, 0.05)",
	narrow: "0px 0px 15px rgba(0,0,0,0.10)",
	header: "0px 0px 50px 0px rgba(0, 0, 0, 0.05)",
	card: "0px 0px 25px 0px rgba(0, 0, 0, 0.05)",
	popup: "0px 0px 100px 0px rgba(0, 0, 0, 0.25)",
	navContent: "0px 4px 4px 0px rgba(0, 0, 0, 0.25)",
};

export const breakpoints = {
	xs: 0,
	sm: 576,
	md: 768,
	lg: 992,
	xl: 1200,
	"2xl": 1440,
};

export const dimensions = {
	// TODO: We should use the styled-system scale
	// See https://github.com/peerigon/newww/pull/568#discussion_r543695604
	spacing: {
		px5: "5px",
		px10: "10px",
		px20: "20px",
		px25: "25px",
		px30: "30px",
		px35: "35px",
		px40: "40px",
		px50: "50px",
		px60: "60px",
		px80: "80px",
		px100: "100px",
		px110: "110px",
		px120: "120px",
		px160: "160px",
		px200: "200px",
		px300: "300px",
		px240: "240px",
		vw5: "5vw",
	},
	widths: {
		px120: "12rem",
		px150: "15rem",
		px900: "90rem",
		px320: "32rem",
	},
	blog: {
		margin: {
			mobile: "15px",
			small: "20px",
			default: "30px",
		},
		grid: {
			marginXs: "10px",
			marginMd: "20px",
			full: "1 / 13",
			wide: "2 / 12",
			center: "3 / 11",
			left: "2 / 4",
		},
	},
	masonry: {
		marginInPX: 30, // px, used for calculations
		imageTile: "340px",
		basicTileInPX: 350, // px, used for calculations
		mobile: {
			image: "100px",
			imageOffset: "-10px",
		},
	},
	zIndex: {
		belowDefault: -1,
		default: 1,
		aboveDefault: 2,
		zoomAnimation: 3,
		header: 4,
		aboveHeader: 5,
	},
	border: {
		underline: "2px solid black",
		noUnderline: `2px solid ${colors.gray.line}`,
		divider: `1px solid ${colors.gray.line}`,
		radius: "15px",
		padding: "2px",
	},
	header: {
		margin: "65px",
		padding: "20px",
	},
	input: {
		padding: "6px 0",
		height: "3.2rem",
	},
	textarea: {
		padding: "16px",
		minHeight: "200px",
	},
	callToActionSection: {
		imageCropFactor: 5,
		imageSize: {
			small: 88,
			medium: 130,
			regular: 180,
			large: 214,
		},
	},
	keyTopicsSection: {
		imageSizeLg: "300px",
	},
};

export const bezier = {
	circle: `cubic-bezier(0.79, 0.14, 0.15, 0.86)`,
	bounce: `cubic-bezier(0.6, -0.28, 0.74, 0.05)`,
	rotate: `cubic-bezier(0.33, 1, 0.68, 1)`,
	smooth: `cubic-bezier(0.4, 0, 0.2, 1)`,
};

export const transitions = {
	fast: `0.2s ${bezier.circle}`,
	regular: `0.3s ${bezier.circle}`,
	bounceBack: `0.3s ${bezier.bounce}`,
	slow: `0.4s ${bezier.circle}`,
	quick: `0.3s ${bezier.rotate}`,
};

export const heights = {
	header: {
		desktop: "85px",
		mobile: "60px",
		mobileInPX: 60,
	},
	pageHeaderSection: {
		mobileInPx: 450,
		desktopInPx: 700,
		minTablet: "65vh",
		minDesktop: "75vh",
		contentDesktop: "70vh",
	},
	pageIlluHeaderSection: {
		mobileInPx: 450,
		desktopInPx: 700,
		minDesktop: "49vh",
		contentDesktop: "45vh",
	},
	locationSection: {
		mobileInPx: 320,
		tabletInPx: 450,
		desktopInPx: 600,
	},
};

export const animations = {
	timing: {
		circleFunction: "cubic-bezier(0.79, 0.14, 0.15, 0.86)",
		cubic: "cubic-bezier(.45,.05,.55,.95)",
		bounce: "cubic-bezier(.68,-0.55,.27,1.55)",
	},
	keyframes: {
		rotate: `
      @keyframes rotate {
        0% {
          transform: rotate(0deg);
        }
        100% {
          transform: rotate(360deg);
        }
      }
    `,
		smallWaves: `
      @keyframes smallWaves {
        0% {
          transform: rotate(1deg);
        }
        50% {
          transform: rotate(-1deg);
        }
        100% {
          transform: rotate(1deg);
        }
      }
    `,
		waves: `
      @keyframes waves {
        0% {
          transform: rotate(3deg);
        }
        50% {
          transform: rotate(-3deg);
        }
        100% {
          transform: rotate(3deg);
        }
      }
    `,
		float: `
      @keyframes float {
        0% {
          transform: translateY(0px);
        }
        50% {
          transform: translateY(-15px);
        }
        100% {
          transform: translateY(0px);
        }
      }
    `,
		buzzOut: `
      @keyframes buzzOut {
        10% {
          transform: translateX(3px) rotate(2deg);
        }
        20% {
          transform: translateX(-3px) rotate(-2deg);
        }
        30% {
          transform: translateX(3px) rotate(2deg);
        }
        40% {
          transform: translateX(-3px) rotate(-2deg);
        }
        50% {
          transform: translateX(2px) rotate(1deg);
        }
        60% {
          transform: translateX(-2px) rotate(-1deg);
        }
        70% {
          transform: translateX(2px) rotate(1deg);
        }
        80% {
          transform: translateX(-2px) rotate(-1deg);
        }
        90% {
          transform: translateX(1px) rotate(0);
        }
        100% {
          transform: translateX(-1px) rotate(0);
        }
      }
    `,
		pulse: `
      @keyframes pulse {
        0% {
          transform: scale(1);
        }
        50% {
          transform: scale(1.1);
        }
        100% {
          transform: scale(1);
        }
      }
    `,
		scrollNudge: `
	@keyframes scrollNudge {
      0% {
        transform: scaleY(0) translate(0, 0);
      }
      50% {
        transform: scaleY(1) translate(0, 0);
      }
      100% {
        transform: scaleY(1) translate(0, 100%);
      }
	}
    `,
	},
};

export const sizeMap: Record<SpacingType, number> = {
	xxxSmall: 0,
	xxSmall: 8,
	xSmall: 12,
	small: 16,
	medium: 32,
	large: 48,
	xLarge: 64,
	xxLarge: 88,
	xxxLarge: 120,
};
